import React from 'react';
import styled from 'styled-components';
import { Table } from 'antd';
import Select from 'react-select';
import Input from 'components/forms/Input';
import InputLabel from '../common/InputLabel';
import FormRow from './FormRow';

const Title = styled.h2`
    font-family: ${({ theme }) => theme.font.title};
    font-size: 1.9em;
    color: ${({ theme }) => theme.colour.dark};
    text-align: left;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
`;

const buildIncrementRow = (
    sellingPrice, // number
    formData,
) => {
    const vatPercentage = parseFloat(formData.vat) || 0;
    const commissionPercentage = parseFloat(formData.commissionPercentage) || 0;
    const costOfOccupiedRoomPerNight = parseFloat(formData.costOfOccupiedRoom) || 0;
    // calculated field, those are the amount for the BASE selling price
    const { totalReductions, vatAmount, commissionAmount } = formData;

    const vat = sellingPrice * (vatPercentage / (100 + vatPercentage));
    const commission = sellingPrice * (commissionPercentage / 100);
    const inclusions = totalReductions - (vatAmount + commissionAmount);
    const costOfOccupiedRoom = costOfOccupiedRoomPerNight * formData.numberOfNights;
    const netProfit = sellingPrice - (vat + commission + inclusions + costOfOccupiedRoom);

    return {
        key: 1,
        sellingPrice: sellingPrice.toFixed(2),
        vat: vat.toFixed(2),
        commission: commission.toFixed(2),
        inclusions: inclusions.toFixed(2),
        costOfOccupiedRoom: costOfOccupiedRoom.toFixed(2),
        netProfit: netProfit.toFixed(2),
    };
};

const buildIncrements = (formData) => {
    const incrementAmount = parseFloat(formData.incrementAmount) || 0;
    const { incrementType, incrementDirection } = formData;

    const proposedRate = parseFloat(formData.proposedRate) || 0;

    if (!incrementAmount) return [buildIncrementRow(proposedRate, formData)];

    const numberOfIncrements = 5;
    const increments = [];

    for (let index = 0; index < numberOfIncrements; index += 1) {
        let sellingPrice = proposedRate;
        if (index !== 0) {
            if (incrementType === 'fixedAmount') {
                let incrementValue = incrementAmount * index;
                if (incrementDirection === 'decrease') incrementValue = -incrementValue;

                sellingPrice = proposedRate + incrementValue;
            } else {
                let percentageOfAmount = proposedRate * (incrementAmount / 100) * index;
                if (incrementDirection === 'decrease') percentageOfAmount = -percentageOfAmount;

                sellingPrice = proposedRate + percentageOfAmount;
            }
        }

        increments.push(buildIncrementRow(sellingPrice, formData));
    }

    return increments;
};

const IncrementsTable = ({ formData, onChangeFieldValue, selectStyles, currency }) => {
    const incrementTypeOptions = [
        { value: 'fixedAmount', label: `${currency}` },
        { value: 'percentage', label: '%' },
    ];
    const incrementTypeSelectedOption = incrementTypeOptions.find((row) => row.value === formData.incrementType);

    const incrementDirectionOptions = [
        { value: 'increase', label: 'Increase' },
        { value: 'decrease', label: 'Decrease' },
    ];
    const incrementDirectionSelectedOption = incrementDirectionOptions.find(
        (row) => row.value === formData.incrementDirection,
    );

    const rowStyle = {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1em',
    };

    const increments = buildIncrements(formData);
    return (
        <div className="pkgCalc-form-increments-section">
            <Title>Increments Table</Title>
            <hr style={{ marginBottom: '2em' }} />
            <FormRow style={rowStyle}>
                <InputLabel style={{ width: '40%' }} text="Fixed Amount or Percentage" />
                <div className="pkgCalc-form-select">
                    <Select
                        options={incrementTypeOptions}
                        value={incrementTypeSelectedOption}
                        styles={selectStyles}
                        onChange={(option) => onChangeFieldValue('incrementType', option.value)}
                    />
                </div>
            </FormRow>
            <FormRow style={rowStyle}>
                <InputLabel style={{ width: '40%' }} text="Direction" />
                <div className="pkgCalc-form-select">
                    <Select
                        options={incrementDirectionOptions}
                        value={incrementDirectionSelectedOption}
                        styles={selectStyles}
                        onChange={(option) => onChangeFieldValue('incrementDirection', option.value)}
                    />
                </div>
            </FormRow>
            <FormRow style={rowStyle}>
                <InputLabel style={{ width: '40%' }} text="Apply Increments of how much" />
                <Input
                    type="number"
                    placeholder="0"
                    value={formData.incrementAmount}
                    onChange={(event) => onChangeFieldValue('incrementAmount', event.target.value)}
                    className="pkgCalc-form-number"
                    addonBefore={currency}
                />
            </FormRow>
            <Table
                bordered
                rowKey="netProfit"
                dataSource={increments}
                columns={[
                    {
                        title: 'Selling Price',
                        dataIndex: 'sellingPrice',
                        width: '14%',
                        render: (sellingPrice) => (
                            <text>
                                {currency}
                                {sellingPrice}
                            </text>
                        ),
                    },
                    {
                        title: 'VAT',
                        dataIndex: 'vat',
                        width: '10%',
                        render: (vat) => (
                            <text>
                                {currency}
                                {vat}
                            </text>
                        ),
                    },
                    {
                        title: 'Commission',
                        dataIndex: 'commission',
                        width: '18%',
                        render: (commission) => (
                            <text>
                                {currency}
                                {commission}
                            </text>
                        ),
                    },
                    {
                        title: 'Inclusions',
                        dataIndex: 'inclusions',
                        width: '14%',
                        render: (inclusions) => (
                            <text>
                                {currency}
                                {inclusions}
                            </text>
                        ),
                    },
                    {
                        title: 'Cost of Occupied Room',
                        dataIndex: 'costOfOccupiedRoom',
                        width: '18%',
                        render: (costOfOccupiedRoom) => (
                            <text>
                                {currency}
                                {costOfOccupiedRoom}
                            </text>
                        ),
                    },
                    {
                        title: 'Net Profit',
                        dataIndex: 'netProfit',
                        width: '18%',
                        render: (netProfit) => (
                            <text>
                                {currency}
                                {netProfit}
                            </text>
                        ),
                    },
                ]}
                pagination={false}
            />
        </div>
    );
};

export default IncrementsTable;
